<template>
  <LineChart
    :request-status="requestStatus"
    :series="series"
    :title="title"
    :item-id="itemId"
    class="make-a-copy"
    :card-options="cardOptions"
    v-bind="$attrs"
    @reload="getData"
  />
</template>

<script>
import LineChart from '@/components/cards/models/LineChart';
import { mapActions } from 'vuex';
import sentimentModel from '@/mixins/sentiment-model';
import cardsModel from '@/mixins/cards-model';
import EventBus from '@/utils/event-bus';
import { DATA_TYPE } from '@/store/modules/cards/constants';
import { POST_FIELDS, OPERATION } from '@/services/charts-service';

export default {
  name: 'SentimenTimeSeriesSacListening',
  components: {
    LineChart,
  },
  mixins: [
    sentimentModel,
    cardsModel,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      requestStatus: 'loading',
      rawSeries: [],
      totalPosts: 0,
      seriesNames: [
        'Positivo',
        'Negativo',
        'Neutro',
      ],
      cardOptions: {
        chart: {
          type: 'areaspline',
        },
        legend: {
          enabled: true,
        },
        plotOptions: {
          areaspline: {
            stacking: 'normal',
            lineWidth: 0.5,
            lineColor: 'black',
            marker: {
              enabled: false,
            },
          },
        },
        tooltip: {
          pointFormat: '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.y}</b><br/>',
        },
        yAxis: {
          labels: {
            format: '{value}',
          },
        },
        xAxis: {},
      },
    };
  },
  computed: {
    series() {
      if (this.rawSeries && this.rawSeries.length) {
        return this.rawSeries.map((rawSerie) => {
          rawSerie.data = rawSerie.data.map((val, index) => [this.categories[index], val || 0]);
          return {
            color: this.getSentimentColor(rawSerie.name),
            ...rawSerie,
          };
        });
      }
      return [];
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getChartsService',
    ]),
    calculateSumSeriesPoints(rawSeries) {
      return rawSeries.reduce((accumulator, { data }) =>
        data.map((item, index) =>
          item + (accumulator[index] || 0)), []);
    },
    orderDataArrayAsc(data) {
      return data.sort();
    },
    setXAxisCategories(categories) {
      const rawCategories = this.orderDataArrayAsc([...categories]);
      const formattedCategories = this.formattedCategoriesForQuarter(rawCategories);

      this.categories = formattedCategories;
    },
    filterByPointName({ name }) {
      return this.seriesNames.includes(name);
    },
    getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }

      const promisses = [
        this.createRequisitions(DATA_TYPE.LISTENING, options),
        this.createRequisitions(DATA_TYPE.SAC, options),
      ];
      Promise.all(
        promisses
      ).then(([responseListening, responseSac]) => {
        this.requestStatus = 'success';
        const response = this.aggregateResponses(responseListening, responseSac);
        if (options?.silentRefresh) {
          EventBus.$emit('updated', { itemId: this.itemId });
        }

        const { points, axes: [axi] } = this.normalizeSentimentResponseFromChartService(response);
        const categoriesSerie = axi?.values || [];
        this.setXAxisCategories(categoriesSerie);
        this.rawSeries = points.filter(this.filterByPointName);
      })
        .catch(() => {
          this.requestStatus = 'error';
        });
    },
    createRequisitions(dataType, options) {
      return this.getChartsService({
        itemId: this.itemId,
        method: 'getTimeSeries',
        filterType: dataType,
        params: {
          grouping: [POST_FIELDS.POLARITY],
          operators: OPERATION.COUNT,
          datePortion: this.getDatePortion(options),
        },
      });
    },
    aggregateResponses(res1, res2) {
      const pointsMap = new Map();

      const addPoints = (points) => {
        points.forEach(({ name, data }) => {
          if (!pointsMap.has(name)) {
            pointsMap.set(name, Array(data.length).fill(0));
          }
          pointsMap.set(name, pointsMap.get(name).map((val, idx) => val + data[idx]));
        });
      };

      addPoints(res1.points);
      addPoints(res2.points);

      const aggregatedPoints = Array.from(pointsMap, ([name, data]) => ({ name, data }));

      return {
        points: aggregatedPoints,
        axes: res1.axes,
      };
    },
  },
};
</script>

